/**
 * Pure decision logic for the inbox reader's inline editor (FRONT-202,
 * widened by FRONT-1107).
 *
 * The inbox chat modal lets the user flip an item into edit mode from the
 * pencil in its header: the heading becomes a name field and the body becomes
 * a text area, both committed by one Save. These helpers keep the two
 * behavioural rules testable without rendering the modal:
 *
 *  - when the Save button is enabled, and
 *  - what the Escape key does while an edit is in progress.
 */

/**
 * Whether Save is offered for the current edit.
 *
 * FRONT-202 gated this on the body alone. FRONT-1107 put the item's NAME in
 * the same edit session, committed by the same Save, so the body-only rule was
 * replaced outright rather than sitting beside this one — two "can I save?"
 * answers for one button is exactly how a surface starts disagreeing with
 * itself.
 *
 * Saveable when the name is not blank, the body passes whatever its own store
 * demands, and at least one of the two actually changed — saving an untouched
 * item would be a pointless write that still bumps `updatedAt` and re-sorts the
 * inbox feed. A name is compared trimmed, since trimmed is what gets persisted:
 * padding a name with spaces is not an edit.
 *
 * `bodyRequired` is the FRONT-202 empty-body rule, now asked per store rather
 * than assumed: a DOCUMENT with an empty body is rejected by its backend, so
 * Save must stay off. An uploaded FILE may legitimately be empty — the app's
 * own "New file" screen creates one from a name alone — and applying the
 * document rule to it left an empty note permanently un-renameable, with a
 * greyed-out Save and nothing on screen saying why.
 */
export function canSaveEdit(args: {
  draft: string;
  body: string;
  nameDraft: string;
  name: string;
  /** True when this item's store rejects an empty body (documents). */
  bodyRequired: boolean;
}): boolean {
  if (args.bodyRequired && args.draft.trim().length === 0) return false;
  const nextName = args.nameDraft.trim();
  if (nextName.length === 0) return false;
  return args.draft !== args.body || nextName !== args.name.trim();
}

export type EscapeAction =
  | 'cancel-edit'
  | 'collapse-research'
  | 'keep-canvas-draft'
  | 'close-modal';

/**
 * What Escape does, in precedence order. It closes the modal everywhere EXCEPT
 * when something inside the modal is itself open on top of the conversation:
 *
 *  - while editing, it only leaves edit mode, so a reflex Esc can't throw away
 *    an in-progress draft together with the whole modal;
 *  - while the deep-research detail panel covers the chat (FRONT-1500), it
 *    collapses that panel back to its one-line bar — the panel is the topmost
 *    thing on screen, so Escape has to peel it off before it can reach the
 *    modal underneath.
 *
 * FRONT-2594: a Canvas document draft with unsaved changes (`canvasDirty`)
 * sits below those two and above closing: Escape is swallowed so a reflex Esc
 * cannot close the window on top of a hand-edited document. (The draft is also
 * kept in localStorage, but a window that vanishes mid-edit still reads as lost
 * work.) The × button still closes.
 *
 * The first two never co-occur in practice (editing is a document/file surface, the
 * research panel is a chat one), but the order is pinned rather than left to
 * chance: losing a draft is the worse outcome of the two.
 */
export function escapeAction(
  isEditing: boolean,
  researchExpanded = false,
  canvasDirty = false,
): EscapeAction {
  if (isEditing) return 'cancel-edit';
  if (researchExpanded) return 'collapse-research';
  if (canvasDirty) return 'keep-canvas-draft';
  return 'close-modal';
}

/**
 * FRONT-2678: whether a click on the window's backdrop may close it. Mirrors
 * Escape: a Canvas document with unsaved edits keeps the window open (the
 * window then closes only from its × button), so the backdrop cannot throw the
 * draft away.
 */
export function backdropMayClose(canvasDirty: boolean): boolean {
  return escapeAction(false, false, canvasDirty) === 'close-modal';
}
